import { useEffect } from "react";
import posthog from "posthog-js";

/**
 * Analytics + Affiliate — one-time bootstrap for Microsoft Clarity + PostHog +
 * Skimlinks (auto-affiliate rewrite of outbound merchant links).
 *
 * All three are opt-in via env vars: if a key is missing the loader is silently
 * skipped. No noise in local dev, no dead scripts for visitors from regions we
 * don't care about right now.
 *
 * Environment (frontend/.env):
 *   REACT_APP_CLARITY_PROJECT_ID   – Microsoft Clarity project ID
 *   REACT_APP_POSTHOG_KEY          – PostHog project API key
 *   REACT_APP_POSTHOG_HOST         – PostHog host (default US cloud)
 *   REACT_APP_SKIMLINKS_ID         – Skimlinks site ID (numeric-X-numeric)
 */

function bootClarity() {
  const id = process.env.REACT_APP_CLARITY_PROJECT_ID;
  if (!id || typeof window === "undefined" || window.__clarity_loaded) return;
  window.__clarity_loaded = true;
  // Official Microsoft Clarity loader
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", id);
}

function bootPostHog() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
  if (!key || typeof window === "undefined" || window.__posthog_loaded) return;
  window.__posthog_loaded = true;
  posthog.init(key, {
    api_host: host,
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    session_recording: { maskAllInputs: false, maskInputOptions: { password: true, email: false } },
    loaded: (ph) => {
      // Attach a stable device ID cookie for cross-session tracking
      const id = ph.get_distinct_id();
      if (id) window.__distinct_id = id;
    },
  });
}

/**
 * Skimlinks — auto-monetizes outbound merchant links across 24k+ programs
 * (Amazon, Apple, JustWatch partner retailers, etc.). One JS include and
 * every affiliate-eligible link on the page gets rewritten automatically.
 * https://skimlinks.com — sign up, they issue a numeric site ID.
 *
 * Env: REACT_APP_SKIMLINKS_ID (e.g. "12345X6789")
 */
function bootSkimlinks() {
  const id = process.env.REACT_APP_SKIMLINKS_ID;
  if (!id || typeof window === "undefined" || window.__skimlinks_loaded) return;
  window.__skimlinks_loaded = true;
  const s = document.createElement("script");
  s.type = "text/javascript";
  s.async = true;
  s.src = `https://s.skimresources.com/js/${id}.skimlinks.js`;
  const head = document.getElementsByTagName("head")[0] || document.body;
  head.appendChild(s);
}

/**
 * Hook mounted once at the App root — no rendered UI.
 */
export default function AnalyticsBoot() {
  useEffect(() => {
    bootClarity();
    bootPostHog();
    bootSkimlinks();
  }, []);
  return null;
}

/**
 * Identify the current logged-in user across analytics providers.
 * Called from AuthContext when a user session loads.
 */
export function identifyUser(user) {
  if (!user) return;
  const { user_id, email, name, auth_provider, subscription_status } = user;
  const props = {
    email,
    name,
    auth_provider,
    subscription_status: subscription_status || "free",
  };
  try { if (window.clarity) window.clarity("identify", user_id, undefined, undefined, name || email); } catch { /* clarity may not be loaded */ }
  try { if (window.__posthog_loaded) posthog.identify(user_id, props); } catch { /* posthog may not be loaded */ }
}

/**
 * Reset identity on logout so the next visitor doesn't inherit it.
 */
export function resetIdentity() {
  try { if (window.__posthog_loaded) posthog.reset(); } catch { /* posthog may not be loaded */ }
}
